import { eventEmitter } from "../eventEmitters/EventEmitters";
import { session } from "../services/session";
import storage from "../util/storage";
import { metricKey, StepMetric } from "./funnelMath";
import { FunnelMode } from "./funnelProgress";
import { OpenState } from "./funnelMembers";
import { noteSpan } from "../util/perfProbe";

/*
 * Where the funnel's numbers come from: ONE call for the whole funnel (not one per box). The demo backend answers it in the browser (src/demo/demoSegment.ts) from the same next-page
 * tally the drawer's "Went to" list uses; the real backend (SessionApi getFunnelSteps, skydive_backend/src/SessionApi/Helper/funnel.ts) answers it from the database, and has been on
 * staging only since 2026-10-18 (docs/FUNNEL_STEP_INDICATORS.md).
 */
export interface DateParams {
  startDate: string;
  endDate: string;
  /** a replay only: ask for the funnel as it stood at this instant of the day (epoch ms) instead of the whole range */
  asOf?: number;
}

const store = new Map<string, StepMetric>();
let inFlight = false;
// Bumped by every clearMetrics(): an answer to a question asked BEFORE the clear (another day, another mode, another funnel) must not repopulate what was just emptied.
let epoch = 0;
let queued: { pages: string[]; params: DateParams; mode: FunnelMode } | null = null;
let warned = false;

let openStates: OpenState[] = [];
/** Where each visitor still onsite is in the funnel, as of the last answer (the dashboard's spotlight is seeded from it). */
export const getOpenStates = (): OpenState[] => openStates;
export const getMetric = (page: string, next: string): StepMetric | undefined => store.get(metricKey(page, next));
export const clearMetrics = () => {
  epoch++;
  store.clear();
  openStates = [];
};

/** `pages` is the funnel in order, ending with the goal. Calls that arrive while one is running are coalesced into ONE follow-up with the latest arguments. */
export const refreshMetrics = async (pages: string[], params: DateParams, mode: FunnelMode): Promise<void> => {
  if (pages.length < 2) return;
  if (inFlight) {
    queued = { pages, params, mode };

    return;
  }
  inFlight = true;
  const askedAt = epoch;
  const sentAt = performance.now();
  try {
    const response: any = await session({
      jsonprc: "2.0",
      method: "getFunnelSteps",
      params: { siteId: storage.get("siteId"), pages, mode, startDate: params.startDate, endDate: params.endDate, ...(typeof params.asOf === "number" ? { asOf: params.asOf } : {}) },
      id: 0,
    });
    noteSpan("funnel:rpc", performance.now() - sentAt);   // the round trip, for the staging build stamp (inert in every other build)
    if (askedAt !== epoch) return;   // the question is out of date: drop the answer (the follow-up below, if any, asks the current one)
    const result = response?.data?.result;
    // the answer is { steps, open }; a bare array (an older answer) is just the steps
    const steps = Array.isArray(result) ? result : result?.steps;
    if (Array.isArray(steps)) {
      steps.forEach((m: StepMetric) => store.set(metricKey(m.page, m.next), m));
      openStates = Array.isArray(result?.open) ? result.open : [];
      eventEmitter.emit("funnelMetricsChanged");
    }
  } catch (e) {
    // A failed fetch leaves the last numbers on screen. Say so once: a guard that fails silently looks exactly like "no data".
    if (!warned) {
      warned = true;
      console.warn("[funnel] could not fetch the step numbers", e);
    }
  } finally {
    inFlight = false;
    const next = queued;
    queued = null;
    if (next) refreshMetrics(next.pages, next.params, next.mode);
  }
};
